import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import {
    Donee,
    useDoneeQuery,
    getDoneeQueryData,
    DONEE_QUERY_KEY,
    appQueryClient,
} from '@givelify/api';
import { Logger, setDefaultStartDate } from '@givelify/utils';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    DONEE_SEARCH_QUERY_NAME,
    useDoneeIdFromQuery,
} from 'router/doneeQueryParam';
import { AppState } from 'store';
import { isLoggedIn } from 'store/user/types';

type DoneeContextValue = {
    donee: Donee | undefined;
    campuses: Donee[] | undefined;
    doneeId: number | undefined;
    mainDoneeId: number | undefined;
    isLoading: boolean;
    isError: boolean;
    loadDonee: () => Promise<Donee | null>;
    setSelectedDonee: (donee: Donee) => void;
};

const DoneeContext = createContext<DoneeContextValue | undefined>(undefined);

export const useDoneeContext = () => {
    const ctx = useContext(DoneeContext);
    if (!ctx) {
        throw new Error('useDoneeContext must be used within a DoneeProvider');
    }
    return ctx;
};

type DoneeProviderProps = {
    children: React.ReactNode;
};

export const DoneeProvider: React.FC<DoneeProviderProps> = ({ children }) => {
    const doneeIdFromQuery = useDoneeIdFromQuery();
    const [, setSearchParams] = useSearchParams();
    const doneeQueryEnabled = useSelector((state: AppState) =>
        isLoggedIn(state),
    );
    const { data, isLoading, isError } = useDoneeQuery(doneeQueryEnabled);

    const campuses = data?.campuses;
    const mainDoneeId = campuses?.[0]?.id;
    const selectedCampusId = doneeIdFromQuery || mainDoneeId;

    const donee = useMemo(() => {
        if (!campuses) return undefined;
        return campuses.find((c) => c.id === selectedCampusId) || campuses[0];
    }, [campuses, selectedCampusId]);

    const doneeId = donee?.id;

    useEffect(() => {
        if (!campuses) return;

        const rawDoneeParam = new URLSearchParams(window.location.search).get(
            DONEE_SEARCH_QUERY_NAME,
        );
        if (!rawDoneeParam) return;

        const shouldClean =
            !doneeIdFromQuery ||
            !campuses.some((c) => c.id === doneeIdFromQuery);

        if (shouldClean) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete(DONEE_SEARCH_QUERY_NAME);
                    return next;
                },
                { replace: true },
            );
        }
    }, [doneeIdFromQuery, campuses, setSearchParams]);

    useEffect(() => {
        if (donee?.createdAt) {
            setDefaultStartDate(donee.createdAt.toString());
        }
    }, [donee?.createdAt]);

    const loadDonee = useCallback(async () => {
        try {
            await appQueryClient.refetchQueries({
                queryKey: [DONEE_QUERY_KEY],
            });
        } catch (error) {
            Logger.log(
                'DoneeProvider: failed to refetch donee data',
                { error: String(error) },
                Logger.LogLevel.ERROR,
            );
            return null;
        }
        const freshData = getDoneeQueryData();
        if (!freshData?.campuses) return null;
        return (
            freshData.campuses.find((c) => c.id === selectedCampusId) ||
            freshData.campuses[0] ||
            null
        );
    }, [selectedCampusId]);

    const setSelectedDonee = useCallback(
        (newDonee: Donee) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (newDonee.id === mainDoneeId) {
                        next.delete(DONEE_SEARCH_QUERY_NAME);
                    } else {
                        next.set(
                            DONEE_SEARCH_QUERY_NAME,
                            newDonee.id.toString(),
                        );
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [mainDoneeId, setSearchParams],
    );

    const value = useMemo(
        () => ({
            donee,
            campuses,
            doneeId,
            mainDoneeId,
            isLoading,
            isError,
            loadDonee,
            setSelectedDonee,
        }),
        [
            donee,
            campuses,
            doneeId,
            mainDoneeId,
            isLoading,
            isError,
            loadDonee,
            setSelectedDonee,
        ],
    );

    return (
        <DoneeContext.Provider value={value}>{children}</DoneeContext.Provider>
    );
};
